'use client';

import { useCallback, useMemo, useState } from 'react';

import {
  CalculatorIcon,
  CarIcon,
  CheckCircleIcon,
  ClockIcon,
  MapPinIcon,
  MessageCircleIcon,
  SparklesIcon,
  TrendingUpIcon,
} from 'lucide-react';

/* -------------------------------------------------------------------------- */
/*  Types & Constants                                                         */
/* -------------------------------------------------------------------------- */

interface EarningsCalculatorProps {
  cityName: string;
  hourlyRate: number;
  whatsappUrl?: string;
  applyHref: string;
}

type Step = 'calculator' | 'results';

type VehicleType = 'economy' | 'comfort' | 'xl';

interface VehicleOption {
  key: VehicleType;
  label: string;
  icon: string;
  multiplier: number;
}

const VEHICLE_OPTIONS: VehicleOption[] = [
  { key: 'economy', label: 'Economy', icon: '🚗', multiplier: 1.0 },
  { key: 'comfort', label: 'Comfort', icon: '🚙', multiplier: 1.2 },
  { key: 'xl', label: 'XL', icon: '🚐', multiplier: 1.4 },
];

const HOURS_PRESETS = [10, 20, 30, 40] as const;

const VARIANCE = 0.15;
const WEEKS_PER_MONTH = 4.33;
const MONTHS_PER_YEAR = 12;

/* -------------------------------------------------------------------------- */
/*  Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function formatZAR(amount: number): string {
  return `R${Math.round(amount).toLocaleString('en-ZA')}`;
}

function calculateEarnings(
  hourlyRate: number,
  hoursPerWeek: number,
  vehicleMultiplier: number,
) {
  const baseWeekly = hourlyRate * hoursPerWeek * vehicleMultiplier;
  const baseMonthly = baseWeekly * WEEKS_PER_MONTH;
  const baseYearly = baseMonthly * MONTHS_PER_YEAR;

  return {
    weekly: {
      low: baseWeekly * (1 - VARIANCE),
      high: baseWeekly * (1 + VARIANCE),
    },
    monthly: {
      low: baseMonthly * (1 - VARIANCE),
      high: baseMonthly * (1 + VARIANCE),
    },
    yearly: {
      low: baseYearly * (1 - VARIANCE),
      high: baseYearly * (1 + VARIANCE),
    },
  };
}

function formatRange(low: number, high: number): string {
  return `${formatZAR(low)} - ${formatZAR(high)}`;
}

/* -------------------------------------------------------------------------- */
/*  Component                                                                  */
/* -------------------------------------------------------------------------- */

export function EarningsCalculator({
  cityName,
  hourlyRate,
  whatsappUrl,
  applyHref,
}: EarningsCalculatorProps) {
  const [step, setStep] = useState<Step>('calculator');
  const [hoursPerWeek, setHoursPerWeek] = useState(20);
  const [vehicleType, setVehicleType] = useState<VehicleType>('economy');

  const selectedVehicle = VEHICLE_OPTIONS.find((v) => v.key === vehicleType)!;
  const earnings = useMemo(
    () =>
      calculateEarnings(hourlyRate, hoursPerWeek, selectedVehicle.multiplier),
    [hourlyRate, hoursPerWeek, selectedVehicle.multiplier],
  );

  const handleSeeReport = useCallback(() => {
    setStep('results');
    setTimeout(() => {
      document
        .getElementById('calculator')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }, []);

  return (
    <section
      id="calculator"
      className="w-full px-4 py-16 sm:py-20"
      style={{ backgroundColor: 'var(--landing-secondary)' }}
    >
      <div className="mx-auto max-w-2xl">
        <div
          className="transition-opacity duration-500 ease-in-out"
          style={{ opacity: 1 }}
        >
          {step === 'calculator' && (
            <CalculatorStep
              cityName={cityName}
              hoursPerWeek={hoursPerWeek}
              vehicleType={vehicleType}
              earnings={earnings}
              onHoursChange={setHoursPerWeek}
              onVehicleChange={setVehicleType}
              onContinue={handleSeeReport}
            />
          )}

          {step === 'results' && (
            <ResultsStep
              cityName={cityName}
              hoursPerWeek={hoursPerWeek}
              vehicleType={vehicleType}
              earnings={earnings}
              whatsappUrl={whatsappUrl}
              applyHref={applyHref}
            />
          )}
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 1: Calculator                                                         */
/* -------------------------------------------------------------------------- */

interface CalculatorStepProps {
  cityName: string;
  hoursPerWeek: number;
  vehicleType: VehicleType;
  earnings: ReturnType<typeof calculateEarnings>;
  onHoursChange: (hours: number) => void;
  onVehicleChange: (type: VehicleType) => void;
  onContinue: () => void;
}

function CalculatorStep({
  cityName,
  hoursPerWeek,
  vehicleType,
  earnings,
  onHoursChange,
  onVehicleChange,
  onContinue,
}: CalculatorStepProps) {
  return (
    <div className="flex flex-col gap-10">
      {/* Section label */}
      <div className="text-center">
        <p className="mb-3 text-sm tracking-[0.2em] text-white/40 uppercase">
          Earnings Calculator
        </p>
        <h2 className="text-3xl font-black text-white sm:text-4xl">
          How Much Could You Earn?
        </h2>
        <p className="mt-2 text-base text-white/60">in {cityName}</p>
      </div>

      {/* Vehicle type selector */}
      <div>
        <label className="mb-3 flex items-center gap-2 text-sm font-medium text-white/60">
          <CarIcon className="h-4 w-4" />
          Vehicle Type
        </label>
        <div className="grid grid-cols-3 gap-3">
          {VEHICLE_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              onClick={() => onVehicleChange(option.key)}
              className={`flex flex-col items-center gap-1.5 rounded-xl border-2 px-3 py-4 text-center transition-all active:scale-[0.97] ${
                vehicleType === option.key
                  ? 'border-[var(--landing-primary)] bg-[var(--landing-primary)]/10'
                  : 'border-white/10 bg-white/5 hover:border-white/20'
              }`}
              data-test={`vehicle-${option.key}`}
            >
              <span className="text-2xl">{option.icon}</span>
              <span className="text-sm font-semibold text-white">
                {option.label}
              </span>
              <span className="text-xs text-white/40">
                {option.multiplier === 1.0
                  ? 'Base'
                  : `+${String(Math.round((option.multiplier - 1) * 100))}%`}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Hours per week */}
      <div>
        <label className="mb-3 flex items-center gap-2 text-sm font-medium text-white/60">
          <ClockIcon className="h-4 w-4" />
          Hours Per Week
        </label>

        {/* Preset buttons */}
        <div className="mb-4 grid grid-cols-4 gap-2">
          {HOURS_PRESETS.map((hours) => (
            <button
              key={hours}
              type="button"
              onClick={() => onHoursChange(hours)}
              className={`rounded-xl border-2 px-3 py-3 text-center text-sm font-semibold transition-all active:scale-[0.97] ${
                hoursPerWeek === hours
                  ? 'border-[var(--landing-primary)] bg-[var(--landing-primary)]/10 text-white'
                  : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'
              }`}
              data-test={`hours-${String(hours)}`}
            >
              {String(hours)}h
            </button>
          ))}
        </div>

        {/* Slider */}
        <div className="relative px-1">
          <input
            type="range"
            min={5}
            max={50}
            step={1}
            value={hoursPerWeek}
            onChange={(e) => onHoursChange(parseInt(e.target.value, 10))}
            className="earnings-calc-slider h-2 w-full cursor-pointer appearance-none rounded-full bg-white/10"
            aria-label="Adjust hours per week"
            data-test="calculator-slider"
          />
          <style
            dangerouslySetInnerHTML={{
              __html: `
                .earnings-calc-slider::-webkit-slider-thumb {
                  -webkit-appearance: none;
                  appearance: none;
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  background: var(--landing-primary);
                  cursor: pointer;
                  border: 3px solid var(--landing-secondary);
                  box-shadow: 0 0 0 2px var(--landing-primary);
                }
                .earnings-calc-slider::-moz-range-thumb {
                  width: 40px;
                  height: 40px;
                  border-radius: 50%;
                  background: var(--landing-primary);
                  cursor: pointer;
                  border: 3px solid var(--landing-secondary);
                  box-shadow: 0 0 0 2px var(--landing-primary);
                }
              `,
            }}
          />
          <div className="mt-2 flex justify-between text-xs text-white/30">
            <span>5 hrs</span>
            <span className="font-semibold text-white/70">
              {String(hoursPerWeek)} hrs/week
            </span>
            <span>50 hrs</span>
          </div>
        </div>
      </div>

      {/* Live earnings preview */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
        <div className="flex items-center justify-center gap-2 text-sm font-medium text-white/60">
          <SparklesIcon
            className="h-4 w-4"
            style={{ color: 'var(--landing-primary)' }}
          />
          Estimated Weekly Earnings
        </div>
        <p
          className="mt-3 text-center text-4xl font-black sm:text-5xl"
          style={{ color: 'var(--landing-primary)' }}
          data-test="live-earnings"
        >
          {formatRange(earnings.weekly.low, earnings.weekly.high)}
        </p>
        <p className="mt-2 text-center text-sm text-white/40">per week</p>
      </div>

      {/* CTA button */}
      <button
        type="button"
        onClick={onContinue}
        className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl text-lg font-bold text-black transition-all hover:opacity-90 active:scale-[0.98]"
        style={{ backgroundColor: 'var(--landing-primary)' }}
        data-test="calculator-cta"
      >
        <CalculatorIcon className="h-5 w-5" />
        See My Full Earnings
      </button>

      {/* Disclaimer */}
      <p className="text-center text-xs text-white/30">
        Estimates based on driver-reported earnings in {cityName}. Actual
        earnings vary based on demand, time, and expenses.
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Step 2: Results                                                            */
/* -------------------------------------------------------------------------- */

interface ResultsStepProps {
  cityName: string;
  hoursPerWeek: number;
  vehicleType: VehicleType;
  earnings: ReturnType<typeof calculateEarnings>;
  whatsappUrl?: string;
  applyHref: string;
}

function ResultsStep({
  cityName,
  hoursPerWeek,
  vehicleType,
  earnings,
  whatsappUrl,
  applyHref,
}: ResultsStepProps) {
  const selectedVehicle = VEHICLE_OPTIONS.find((v) => v.key === vehicleType)!;

  return (
    <div className="flex flex-col gap-8">
      {/* Header */}
      <div className="text-center">
        <div
          className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
          style={{ backgroundColor: 'var(--landing-primary)' }}
        >
          <CheckCircleIcon className="h-8 w-8 text-black" />
        </div>
        <h2 className="text-3xl font-black text-white sm:text-4xl">
          Your Estimated Earnings
        </h2>
        <p className="mt-2 text-base text-white/60">
          Based on {String(hoursPerWeek)} hours/week driving{' '}
          {selectedVehicle.label} in {cityName}
        </p>
      </div>

      {/* Full earnings breakdown */}
      <div className="grid gap-4 sm:grid-cols-3">
        <EarningsCard
          label="Weekly"
          range={formatRange(earnings.weekly.low, earnings.weekly.high)}
          accent={false}
        />
        <EarningsCard
          label="Monthly"
          range={formatRange(earnings.monthly.low, earnings.monthly.high)}
          accent={true}
        />
        <EarningsCard
          label="Yearly"
          range={formatRange(earnings.yearly.low, earnings.yearly.high)}
          accent={false}
        />
      </div>

      {/* Peak hours bonus */}
      <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
        <div className="flex items-start gap-3">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
            style={{ backgroundColor: 'var(--landing-accent)', opacity: 0.15 }}
          >
            <TrendingUpIcon
              className="h-5 w-5"
              style={{ color: 'var(--landing-accent)' }}
            />
          </div>
          <div>
            <h3 className="text-base font-semibold text-white">
              Peak Hours Bonus
            </h3>
            <p className="mt-1 text-sm text-white/60">
              Earn 1.5x to 2.5x during peak periods: weekday mornings (6:00 to
              9:00), evenings (16:00 to 19:00), and Friday/Saturday nights.
              Surge pricing in {cityName} can significantly increase your
              earnings above these estimates.
            </p>
          </div>
        </div>
      </div>

      {/* Apply CTAs */}
      <div className="flex flex-col gap-3">
        {whatsappUrl && (
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-xl text-lg font-bold text-black transition-all hover:opacity-90 active:scale-[0.98]"
            style={{ backgroundColor: 'var(--landing-primary)' }}
            data-test="calc-whatsapp-cta"
          >
            <MessageCircleIcon className="h-5 w-5" />
            Apply via WhatsApp
          </a>
        )}
        <a
          href={applyHref}
          className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-xl border border-white/20 bg-white/5 text-sm font-semibold text-white transition-all hover:bg-white/10 active:scale-[0.98]"
          data-test="calc-form-cta"
        >
          Apply with Form
        </a>
      </div>

      {/* Location indicator */}
      <p className="flex items-center justify-center gap-1.5 text-center text-xs text-white/30">
        <MapPinIcon className="h-3 w-3" />
        Earnings estimate for {cityName}, South Africa
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Sub-components                                                             */
/* -------------------------------------------------------------------------- */

interface EarningsCardProps {
  label: string;
  range: string;
  accent: boolean;
}

function EarningsCard({ label, range, accent }: EarningsCardProps) {
  return (
    <div
      className={`rounded-2xl border p-5 text-center ${
        accent
          ? 'border-[var(--landing-primary)]/30 bg-[var(--landing-primary)]/10'
          : 'border-white/10 bg-white/5'
      }`}
    >
      <p className="text-xs tracking-wider text-white/50 uppercase">{label}</p>
      <p
        className={`mt-2 text-xl font-bold sm:text-2xl ${
          accent ? '' : 'text-white'
        }`}
        style={accent ? { color: 'var(--landing-primary)' } : undefined}
      >
        {range}
      </p>
    </div>
  );
}
